/**
 * CRMObjectFullPageView - Main full-page view for CRM records.
 *
 * Composes all sub-components into a two-column layout with semantic structure.
 * Left column: Header, AI summary, contact overview (scrolls independently)
 * Right column: Metric cards, Activity/Emails/Notes/Files tabs (plus Meeting Prep and Intelligence when applicable), chat input pinned at bottom
 */

import { LayoutRight, Mail01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { UploadedFile } from '@/components/files';
import { LockedComposer } from '@/components/seat-gate/LockedComposer';
import { useEmailCompose } from '@/contexts/EmailComposeContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCanWrite, useFeatureAccess } from '@/hooks/usePermissions';
import { useStartNewConversation } from '@/hooks/useStartNewConversation';
import { Button } from '@/lib/base/buttons/button';
import { UnsavedChangesModal } from '@/lib/base/overlays/unsaved-changes-modal';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { EmailThreadVisibilityBadge } from '@/lib/crm/components/EmailThreadVisibilityBadge';
import { SaveBar } from '@/lib/crm/components/SaveBar';
import { useFileAssociation } from '@/lib/crm/hooks/useFileAssociation';
import { useRestrictedSubscription } from '@/lib/crm/realtime';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { parseMentions } from '@/lib/search/parseMentions';
import { getPropertyValue } from '@/types/crm';

import { CrmObjectAvatar } from '../../components/CrmObjectAvatar';
import { RECORD_RESTRICTED_ENTITY_TYPES } from '../../core/entityTypes';
import type { CrmObject } from '../../core/types';
import { contactListConfig } from '../../list/config/contactListConfig';
import { emailThreadListConfig } from '../../list/config/emailThreadListConfig';
import { FilesTabContent } from '../files';
import { useMeetingVisibilityActions } from '../hooks/useMeetingVisibilityActions';
import { useOverviewEditState } from '../hooks/useOverviewEditState';
import { useRailCollapsed } from '../hooks/useRailCollapsed';
import {
  computeRailMaxWidth,
  RAIL_MIN_WIDTH,
  useRailWidth,
} from '../hooks/useRailWidth';
import { type EntityType, serializeMention } from '../notes/mentionUtils';
import {
  type EntityTypeConfig,
  getEntityTypeConfig,
} from '../registry/entityTypeRegistry';
import { ResearchTab } from '../research';
import { OverviewSection } from '../sections/overview-section';
import { TASK_ENTITY_TYPE } from '../task/task.constants';
import { TimelineTab } from '../timeline';
import { ALL_EVENT_TYPES } from '../timeline/timelineFilterDefaults';
import { TimelineFilters as TimelineFiltersControl } from '../timeline/TimelineFilters';
import type { TimelineFilters } from '../timeline/types';
import { AssociationsTab } from './AssociationsTab';
import { FullPageTabs, type FullPageTabType } from './FullPageTabs';
import {
  ACTION_BUTTON_CLASS,
  COMPACT_TOOLBAR_BUTTON_CLASS,
  HeaderActions,
} from './HeaderActions';
import { MeetingNotesTab } from './MeetingNotesTab';
import { MeetingPrepTab } from './MeetingPrepTab';
import { MeetingsTab } from './MeetingsTab';
import { OverviewTab } from './OverviewTab';
import { RecordActionMenu } from './RecordActionMenu';
import { RecordNotesListTab } from './RecordNotesListTab';
import { RecordObjectListTab } from './RecordObjectListTab';
import { RecordTasksListTab } from './RecordTasksListTab';

const BADGE_ENTITY_TYPES = new Set([
  'ahoy_email_thread',
  'ahoy_meeting',
  'ahoy_meeting_notes',
  'ahoy_file',
]);

// Tabs that embed a full list table (which owns its own height + scroll), so
// FullPageTabs renders them in a bounded flex container rather than the padded
// scroll area. Module-scope for stable identities.
//
// The Meetings tab is deliberately NOT here: it renders `MeetingsTab`, a padded
// card list, not an embedded list table. A fill tab suppresses the top bar's
// own `border-b` on the assumption the embedded table draws the divider via its
// toolbar `border-t` — but a card list has no such toolbar, so listing meetings
// here left the tab strip with no divider at all. As a padded (non-fill) tab it
// draws the standard `border-b`, matching Overview/Activity/Files.
//
// Notes: meeting records keep their bespoke "Meeting Summaries" view in the
// Notes tab, so notes is only a list table for non-meeting records.
const LIST_TABLE_TABS: ReadonlySet<FullPageTabType> = new Set<FullPageTabType>([
  'contacts',
  'emails',
]);
const LIST_TABLE_TABS_WITH_NOTES: ReadonlySet<FullPageTabType> =
  new Set<FullPageTabType>(['contacts', 'emails', 'notes', 'tasks']);

// The sr-only subtitle renders a raw property value (e.g. ahoy_job_title),
// which can contain a backend entity tag. Strip it to plain text instead of
// leaking the tag markup into the accessibility tree. Mirrors
// renderTextWithMentions's segment-stitching loop (parseMentions.ts) but
// produces a plain string, which is all an sr-only text node needs.
function sanitizeSubtitle(text: string): string {
  const mentions = parseMentions(text);
  if (mentions.length === 0) {
    return text;
  }
  let result = '';
  let lastIndex = 0;
  for (const mention of mentions) {
    result += text.slice(lastIndex, mention.startIndex);
    result += mention.name || 'Unavailable';
    lastIndex = mention.endIndex;
  }
  result += text.slice(lastIndex);
  return result;
}

export interface CRMObjectFullPageViewProps {
  /** The CRM object */
  object: CrmObject;
  /**
   * Runtime-derived config for the entity. Used for custom org_* entities
   * whose config is built from schema at view time. When omitted, falls back
   * to the static registry entry for object.entity_type_name (transitional —
   * pass config explicitly once all consumers are migrated).
   * @default getEntityTypeConfig(object.entity_type_name)
   */
  config?: EntityTypeConfig;
}

/**
 * Main full-page view component for CRM records.
 *
 * Provides a comprehensive two-column layout for viewing and interacting
 * with CRM records outside of the slideout context.
 */
export function CRMObjectFullPageView({
  object,
  config: configProp,
}: CRMObjectFullPageViewProps) {
  // Validate config exists
  const config = useMemo(() => {
    if (configProp) {
      return configProp;
    }
    try {
      return getEntityTypeConfig(object.entity_type_name);
    } catch {
      return null;
    }
  }, [configProp, object.entity_type_name]);

  // Check if research feature is enabled (must be before early return)
  const isResearchEnabled = useFeatureFlag('research_feature');

  // Seat gates (before the early return). Drafting an email is a write, so it
  // follows the seat's write grant; the composer follows the Chat feature and
  // renders the shared locked facade for a seat without it.
  const canWrite = useCanWrite();
  const { hasFeature } = useFeatureAccess();
  const hasChatFeature = hasFeature('chat');
  const navigate = useNavigate();
  const handleLockedComposerActivate = useCallback(() => {
    navigate(`/${object.org_id}/conversations`);
  }, [navigate, object.org_id]);

  // Restrict/share actions for a meeting's access badge — same gate as the
  // slideout's MeetingSubHeader; undefined for every other entity type.
  const visibilityActions = useMeetingVisibilityActions(object);

  // Right-hand details rail: persisted collapsed state. (Hooks must run
  // before the early return.)
  const rail = useRailCollapsed();
  const isDesktop = useBreakpoint('lg');
  const railWidth = useRailWidth(isDesktop);

  // Keep the timeline/tabs' restricted-type content fresh via live WS
  // updates, matching CalendarPage's use of the calendar-scoped set.
  useRestrictedSubscription(RECORD_RESTRICTED_ENTITY_TYPES);

  // Schema-driven fallback for the Files tab gate below: FALLBACK_CONFIG
  // (custom case/person-kind entities) omits 'files' from config.tabs, so
  // this resolves whether a file association exists for the entity type.
  const fileAssociation = useFileAssociation(
    object.org_id,
    object.entity_type_name
  );

  const { openEmailCompose } = useEmailCompose();
  const { startNewConversation } = useStartNewConversation();
  const [message, setMessage] = useState('');
  const [chatError, setChatError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Shared edit-mode plumbing for OverviewSection. The full-page view IS
  // the navigation surface (no outer modal/slideout wraps it), so we leave
  // the defaults on — router and browser-close navigation are blocked when
  // pending edits exist. Same hook is used by SingleSlideout so the slideout
  // and full-page view share one save/cancel UX.
  const { saveBarState, setSaveBarState, modalProps } = useOverviewEditState();

  const entityId = object.entity_id ?? '';
  const entityTypeName = object.entity_type_name;
  // Fallback resolves through the shared display-name util — never the raw
  // entity id (this string becomes a mention chip label in chat).
  const nameForMention = useMemo(
    () => config?.getDisplayName(object) ?? getDisplayName(object),
    [config, object]
  );

  // Activity-tab filter state is lifted to the parent so the filter
  // trigger can render in the tab strip's trailing slot (right of the
  // tab labels) — rather than sitting in its own row below the tabs
  // and pushing tab content down. The `TimelineTab` for the activity
  // panel runs in controlled mode against this state.
  // Shared with the filter control's "All" reset so it restores the same
  // related-activity default the state was seeded with (never `false`).
  const activityDefaultIncludeAssociated =
    config?.timelineConfig?.defaultFilters?.includeAssociated ?? true;
  const [activityFilters, setActivityFilters] = useState<TimelineFilters>(
    () => {
      const seed = config?.timelineConfig?.defaultFilters ?? {};
      return {
        eventTypes: seed.eventTypes ?? ALL_EVENT_TYPES,
        startDate: seed.startDate ?? null,
        endDate: seed.endDate ?? null,
        actorId: seed.actorId ?? null,
        includeAssociated: activityDefaultIncludeAssociated,
      };
    }
  );

  // First non-empty email on a contact. Used to gate the "Compose email" CTA —
  // we only show it when there's actually a recipient to draft for.
  const contactEmail = useMemo(() => {
    if (entityTypeName !== 'ahoy_contact') {
      return null;
    }
    const raw = getPropertyValue<string | string[]>(
      object.properties?.ahoy_email
    );
    const candidates = Array.isArray(raw) ? raw : [raw];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  }, [entityTypeName, object.properties]);

  // On the full-page view, the Gmail external link is replaced by an in-page
  // "Compose email" CTA that pre-fills the ConversationInput at the bottom of
  // the right column. Filter the registry-defined gmailAction out here. The
  // slideout still uses the unfiltered config.headerActions, so its Gmail icon
  // behaviour is unchanged.
  const headerActions = useMemo(
    () => (config?.headerActions ?? []).filter(a => a.id !== 'gmail'),
    [config]
  );

  const handleComposeEmail = useCallback(() => {
    openEmailCompose({
      to: contactEmail ? [contactEmail] : undefined,
      recipientEntity: {
        entityId,
        entityTypeName,
        displayName: nameForMention,
      },
    });
  }, [
    openEmailCompose,
    contactEmail,
    entityId,
    entityTypeName,
    nameForMention,
  ]);

  const submitMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || submitting) {
        return;
      }
      if (!entityId) {
        return;
      }
      setSubmitting(true);
      setChatError(null);
      const mention = serializeMention(
        entityId,
        nameForMention,
        entityTypeName as EntityType
      );
      const fullPrompt = `${text}\n\nContext: ${mention}`;
      // startNewConversation signals failure by invoking its setError callback
      // rather than throwing, so the try/catch alone can't gate the cleanup.
      let failed = false;
      const reportError = (err: string) => {
        failed = true;
        setChatError(err);
      };
      try {
        const fileIds = uploadedFiles.map(f => f.id);
        await startNewConversation(fullPrompt, reportError, fileIds);
        if (!failed) {
          setUploadedFiles([]);
          setMessage('');
        }
      } catch {
        setChatError("We couldn't start the conversation. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      entityId,
      nameForMention,
      entityTypeName,
      uploadedFiles,
      startNewConversation,
    ]
  );

  const handleChatSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await submitMessage(message);
    },
    [message, submitMessage]
  );

  const [, setSearchParams] = useSearchParams();
  const handleViewAllActivity = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'activity');
      return next;
    });
  }, [setSearchParams]);

  // Clicking the record name jumps back to the Overview tab (its default home).
  const handleGoToOverview = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'overview');
      return next;
    });
  }, [setSearchParams]);

  const renderTabActions = useCallback(
    (tab: FullPageTabType) =>
      tab === 'activity' ? (
        <TimelineFiltersControl
          filters={activityFilters}
          onFiltersChange={setActivityFilters}
          defaultIncludeAssociated={activityDefaultIncludeAssociated}
        />
      ) : null,
    [activityFilters, activityDefaultIncludeAssociated]
  );

  // Early return AFTER all hooks
  if (!config) {
    return (
      <div className='flex items-center justify-center min-h-full'>
        <div className='text-center'>
          <h1 className='text-heading-2 mb-2 text-[var(--color-text-primary)]'>
            Unsupported record type
          </h1>
          <p className='text-body text-secondary'>
            This record type is not configured for full-page view.
          </p>
        </div>
      </div>
    );
  }

  const displayName = config.getDisplayName(object);

  const isContactOrCompany =
    object.entity_type_name === 'ahoy_contact' ||
    object.entity_type_name === 'ahoy_company';
  const showResearch = isContactOrCompany && isResearchEnabled;

  // Note: recordType is only used when showResearch is true, which excludes deals via isContactOrCompany guard.
  // The research subsystem only supports 'contact' | 'company' — deals are intentionally excluded.
  // Explicit type annotation ensures TypeScript understands the narrowed type from the guard above.
  const recordType: 'contact' | 'company' =
    object.entity_type_name === 'ahoy_contact' ? 'contact' : 'company';

  const rawSubtitle = config.getSubtitle?.(object) ?? null;
  const subtitle = rawSubtitle !== null ? sanitizeSubtitle(rawSubtitle) : null;

  return (
    <main className='min-h-full lg:h-full lg:overflow-hidden flex flex-col bg-[var(--color-bg-primary)]'>
      {/* Main content container. The top padding lives on the COLUMNS (left
          section + rail content) rather than here, so the rail's left border
          (the panel divider) can run the full height — all the way to the very
          top edge — instead of starting below a shared `pt-4` gap. */}
      <div className='px-6 flex-1 min-h-0'>
        <div className='flex flex-col gap-6 lg:flex-row lg:gap-0 h-full'>
          {/* Main column: record header (identity + actions) + tabs + chat.
              The property details that used to live here moved to the right
              rail (below) to mirror Lightfield's "Account details" layout.
              `pt-4` here (rather than on the shared wrapper) gives the header
              its breathing room while leaving the divider free to reach the top. */}
          <section
            aria-label='Activity'
            className={`flex-1 min-w-0 flex flex-col pt-4 ${
              rail.collapsed ? '' : 'lg:pr-6'
            }`}
          >
            {/* Tabs area fills the column. The record identity + actions ride
                in the tab strip's leading/trailing slots so the whole thing
                reads as one compact top bar (Lightfield style). */}
            <div className='flex-1 min-h-0'>
              <FullPageTabs
                defaultTab='overview'
                tabActions={renderTabActions}
                fillTabs={
                  object.entity_type_name === 'ahoy_meeting'
                    ? LIST_TABLE_TABS
                    : LIST_TABLE_TABS_WITH_NOTES
                }
                leading={
                  <div className='flex items-center gap-2.5 min-w-0'>
                    {/* Avatar links to the Overview tab too (same as the name). */}
                    <button
                      type='button'
                      onClick={handleGoToOverview}
                      aria-label={`${displayName} – overview`}
                      className='shrink-0 rounded-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
                    >
                      {/* Native `sm` is 32px with correctly-tuned initials;
                          no size override / no `block` (which would break the
                          avatar's flex centering of the initials). */}
                      <CrmObjectAvatar object={object} size='sm' />
                    </button>
                    <h1 className='min-w-0 text-base font-semibold tracking-tight text-fg-primary leading-tight'>
                      {/* The name links back to the Overview tab (its default
                          home). Inherits the heading typography via preflight;
                          truncates within the bar. */}
                      <button
                        type='button'
                        onClick={handleGoToOverview}
                        className='block max-w-full truncate text-left cursor-pointer rounded-sm hover:underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
                      >
                        {displayName}
                      </button>
                      {/* Subtitle (job title / domain / amount) is shown in the
                          details rail; kept here sr-only so the heading still
                          announces it and the bullet-separator tests pass. */}
                      {subtitle && (
                        <span className='sr-only'> &bull; {subtitle}</span>
                      )}
                    </h1>
                    {object.access_summary != null &&
                      BADGE_ENTITY_TYPES.has(object.entity_type_name) && (
                        <EmailThreadVisibilityBadge
                          access_summary={object.access_summary}
                          visibilityActions={visibilityActions}
                        />
                      )}
                  </div>
                }
                trailing={
                  <>
                    {canWrite && contactEmail && (
                      <Button
                        size='sm'
                        color='secondary'
                        iconLeading={Mail01}
                        onClick={handleComposeEmail}
                        aria-label={`Draft email to ${displayName}`}
                        // Narrower + more delicate than the shared compact
                        // class: tighter horizontal padding (px-3 → px-2) and a
                        // smaller text-xs label so the CTA reads light in the
                        // top bar.
                        className={`${COMPACT_TOOLBAR_BUTTON_CLASS} !px-2 !text-xs`}
                      >
                        Draft email
                      </Button>
                    )}
                    <HeaderActions actions={headerActions} object={object} />
                    <RecordActionMenu
                      object={object}
                      entityTypeName={object.entity_type_name}
                      viewContext='fullPage'
                      listRoute={config.routes?.list ?? `/:orgId/crm`}
                    />
                    {/* When the details rail is collapsed it takes its in-rail
                        toggle with it, so surface a re-open control here. */}
                    {rail.collapsed && (
                      <Tooltip title='Show details' placement='bottom'>
                        <TooltipTrigger
                          onPress={rail.toggleCollapsed}
                          aria-label='Show details panel'
                          aria-pressed={false}
                          className={ACTION_BUTTON_CLASS}
                        >
                          {/* 12px to match the delicate top-rail icons. */}
                          <LayoutRight className='size-5' aria-hidden='true' />
                        </TooltipTrigger>
                      </Tooltip>
                    )}
                  </>
                }
                overviewContent={
                  <OverviewTab
                    object={object}
                    config={config}
                    onViewAllActivity={handleViewAllActivity}
                  />
                }
                activityContent={
                  // Controlled-mode TimelineTab: filter state lives on
                  // the page so the trigger renders in the tab strip's
                  // trailing slot. `hideFilters` suppresses the
                  // duplicate in-content filter button.
                  <TimelineTab
                    entityId={object.entity_id || ''}
                    orgId={String(object.org_id)}
                    entityType={object.entity_type_name}
                    filters={activityFilters}
                    onFiltersChange={setActivityFilters}
                    hideHeader
                    hideFilters
                    noBorder
                  />
                }
                contactsContent={
                  config.tabs?.includes('contacts') ? (
                    <RecordObjectListTab
                      baseConfig={contactListConfig}
                      entityId={object.entity_id || ''}
                      hostEntityType={object.entity_type_name}
                      countLabel={`contacts for ${displayName}`}
                      countLabelSingular={`contact for ${displayName}`}
                    />
                  ) : undefined
                }
                emailsContent={
                  config.tabs?.includes('emails') ? (
                    <RecordObjectListTab
                      baseConfig={emailThreadListConfig}
                      entityId={object.entity_id || ''}
                      hostEntityType={object.entity_type_name}
                      countLabel={`threads for ${displayName}`}
                      countLabelSingular={`thread for ${displayName}`}
                    />
                  ) : undefined
                }
                meetingsContent={
                  config.tabs?.includes('meetings') ? (
                    <MeetingsTab
                      orgId={object.org_id}
                      entityId={object.entity_id || ''}
                    />
                  ) : undefined
                }
                filesContent={
                  // Registered types (contact/company/deal/meeting) keep the
                  // synchronous config gate to avoid a load-time flash; custom
                  // case/person-kind entities fall through to the
                  // schema-driven resolver since FALLBACK_CONFIG.tabs omits
                  // 'files'. FilesTabContent self-hides if unsupported.
                  config.tabs?.includes('files') ||
                  fileAssociation.association != null ? (
                    <FilesTabContent
                      entityId={object.entity_id || ''}
                      entityTypeName={object.entity_type_name}
                      orgId={object.org_id}
                    />
                  ) : undefined
                }
                prepContent={
                  object.entity_type_name === 'ahoy_meeting' ? (
                    <MeetingPrepTab
                      orgId={object.org_id}
                      meetingEntityId={object.entity_id || ''}
                      meetingTitle={
                        getPropertyValue<string>(
                          object.properties?.ahoy_title
                        ) || undefined
                      }
                    />
                  ) : undefined
                }
                notesContent={
                  object.entity_type_name === 'ahoy_meeting' ? (
                    <MeetingNotesTab
                      orgId={object.org_id}
                      meetingEntityId={object.entity_id || ''}
                      meetingStartTime={
                        getPropertyValue<string>(
                          object.properties?.ahoy_start_time
                        ) || undefined
                      }
                    />
                  ) : (
                    <RecordNotesListTab
                      entityId={object.entity_id || ''}
                      hostEntityType={object.entity_type_name}
                      countLabel={`notes for ${displayName}`}
                      countLabelSingular={`note for ${displayName}`}
                    />
                  )
                }
                tasksContent={
                  object.entity_type_name !== 'ahoy_meeting' &&
                  object.entity_type_name !== TASK_ENTITY_TYPE ? (
                    <RecordTasksListTab
                      entityId={object.entity_id || ''}
                      hostEntityType={object.entity_type_name}
                      countLabel={`tasks for ${displayName}`}
                      countLabelSingular={`task for ${displayName}`}
                    />
                  ) : undefined
                }
                associationsContent={
                  config.tabs?.includes('associations') ? (
                    <AssociationsTab object={object} />
                  ) : undefined
                }
                intelligenceContent={
                  showResearch ? (
                    <ResearchTab
                      orgId={object.org_id}
                      entityId={object.entity_id || ''}
                      recordType={recordType}
                      object={object}
                    />
                  ) : undefined
                }
              />
            </div>

            {/* Chat input - pinned at bottom of the main column */}
            <div className='shrink-0 border-t border-[var(--color-border-secondary)] px-6 py-4 bg-[var(--color-bg-primary)]'>
              <div className='mx-auto w-full max-w-[700px]'>
                {hasChatFeature ? (
                  <ConversationInput
                    value={message}
                    onChange={setMessage}
                    onSubmit={handleChatSubmit}
                    placeholder={`Draft an email, research a deal, ask a question about ${displayName}`}
                    loading={submitting}
                    error={chatError}
                    variant='simple'
                    sticky={false}
                    enableVoice={true}
                    orgId={object.org_id}
                    uploadedFiles={uploadedFiles}
                    onFilesChange={setUploadedFiles}
                    // Stay at the single-row height until the text actually
                    // wraps — matches the chat input behaviour on
                    // ConversationsHome / DashboardHome. Without this, every
                    // keystroke pre-expanded the input to its multi-line
                    // height even when there was nothing to wrap yet.
                    compactUntilWrap
                  />
                ) : (
                  <LockedComposer onActivate={handleLockedComposerActivate} />
                )}
              </div>
            </div>
          </section>

          {/* Right rail: record property details. Moved here from the left
              column to mirror Lightfield's "Account details" layout — the main
              column leads with the record header + tabs. The panel border is
              the section's own full-height `border-l` (rather than a separate
              stretched divider, which could come up short at the top) so it
              spans cleanly top-to-bottom. The two panes carry their own inner
              padding (main `lg:pr-6`, rail `lg:pl-6`) so the border reads as a
              crisp panel edge. The section title, Edit, and the rail-collapse
              control share the section's own header row (the overview is not
              collapsible here — hiding it on the full-page record serves no
              purpose).
              The rail's left edge is drag-resizable via `EdgeResizeHandle`: a
              grip pill fades in on hover/focus (and stays visible while
              dragging), Arrow keys fine-tune the width, and double-click /
              Home resets to the 340px default. The chosen width persists to
              localStorage via `useRailWidth` and the main column (`flex-1`)
              absorbs the change. Desktop only — the mobile layout stacks the
              rail full-width below the main column. */}
          {!rail.collapsed && (
            <section
              aria-label={`${config.displayName} details`}
              // The left border is the full-height panel edge between the main
              // column and the details rail. Use border-PRIMARY (not the fainter
              // secondary used for inline separators) so the divider stays visible
              // the whole way up — alongside the top bar it would otherwise sit
              // white-on-white and appear to start only below the header row.
              //
              // Dynamic drag-computed pixel width has no static Tailwind equivalent —
              // the sanctioned inline-style exception (FRONTEND_PATTERNS § inline styles).
              // Unlike ReportWizard's ResizablePanel, this single render site deliberately
              // applies the style directly rather than extracting a wrapper component.
              // Desktop only; w-full governs mobile.
              style={isDesktop ? { width: railWidth.width } : undefined}
              className='relative w-full lg:shrink-0 lg:h-full lg:pl-6 lg:border-l border-[var(--color-border-primary)] flex flex-col'
            >
              {isDesktop && (
                <EdgeResizeHandle
                  edge='left'
                  onResizeStart={railWidth.handleResizeStart}
                  onKeyboardResize={railWidth.resizeBy}
                  width={railWidth.width}
                  minWidth={RAIL_MIN_WIDTH}
                  // Min-wins normalization mirrors useResizablePanel's clamp so the
                  // announced range matches what resizing can actually reach.
                  maxWidth={computeRailMaxWidth()}
                  isResizing={railWidth.isResizing}
                  onDoubleClick={railWidth.resetWidth}
                  onReset={railWidth.resetWidth}
                  aria-label='Resize details panel. Use left and right arrow keys to adjust width.'
                />
              )}
              {/* pt-4 keeps the "{Type} overview" header aligned with the main
                  column's top bar; the section's border-l above it runs to the top. */}
              <div className='flex-1 lg:overflow-y-auto pt-4 pb-24'>
                <OverviewSection
                  object={object}
                  collapsible={false}
                  onSaveStateChange={setSaveBarState}
                  headerTrailing={
                    <Tooltip title='Hide details' placement='bottom'>
                      <TooltipTrigger
                        onPress={rail.toggleCollapsed}
                        aria-label='Hide details panel'
                        aria-pressed={true}
                        // Nudge 8px right so it lands at the same horizontal
                        // position as the collapsed-state toggle in the top bar.
                        className={`${ACTION_BUTTON_CLASS} translate-x-[8px]`}
                      >
                        <LayoutRight className='size-5' aria-hidden='true' />
                      </TooltipTrigger>
                    </Tooltip>
                  }
                />
              </div>
              {/* Floating SaveBar — overlays the bottom of the details rail
                  when pending edits exist. Same component + positioning as
                  the slideout for visual consistency. */}
              {saveBarState && (
                <SaveBar
                  changeCount={saveBarState.changeCount}
                  onSave={saveBarState.onSave}
                  onCancel={saveBarState.onCancel}
                  isSaving={saveBarState.isSaving}
                  error={saveBarState.error}
                  blockedByConflicts={saveBarState.blockedByConflicts}
                  blockedReason={saveBarState.blockedReason}
                />
              )}
            </section>
          )}
        </div>
      </div>
      {/* Navigation guard — prompts before leaving the page (router link
          or browser close/refresh) while OverviewSection has pending
          edits. Routed through useOverviewEditState above. The slideout
          relies on its own outer close/back guarding instead. */}
      <UnsavedChangesModal {...modalProps} />
    </main>
  );
}
